import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import PhotoCompareSlider from '../components/PhotoCompareSlider';
import ShareGalleryModal from '../components/ShareGalleryModal';
import SelectionViewer from '../components/SelectionViewer';
import CuratorView from '../components/CuratorView';
import MagicDesigner from '../components/MagicDesigner';
import BookView from '../components/BookView';
import {
  Images,
  Loader2,
  ArrowLeft,
  Upload,
  Trash2,
  ExternalLink,
  Copy,
  Heart,
  Sparkles,
  X,
  Check,
  Tag,
  CheckCircle,
  Clock,
  XCircle,
  FileCheck,
  Filter,
  MoreVertical,
  History,
  GitCompare,
  Share2,
  QrCode,
  ImageIcon,
  Star,
  BookOpen,
  BarChart3,
  Wand2,
  DollarSign,
  Volume2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Photo labels configuration
const PHOTO_LABELS = {
  red: { name: 'Czerwony', color: '#EF4444' },
  yellow: { name: 'Żółty', color: '#EAB308' },
  green: { name: 'Zielony', color: '#22C55E' },
  blue: { name: 'Niebieski', color: '#3B82F6' },
  purple: { name: 'Fioletowy', color: '#A855F7' },
  orange: { name: 'Pomarańczowy', color: '#F97316' },
  pink: { name: 'Różowy', color: '#EC4899' }
};

// Approval status configuration
const APPROVAL_STATUSES = {
  draft: { name: 'Szkic', icon: Clock, color: 'bg-gray-500' },
  in_review: { name: 'Do przeglądu', icon: FileCheck, color: 'bg-yellow-500' },
  approved: { name: 'Zatwierdzony', icon: CheckCircle, color: 'bg-green-500' },
  rejected: { name: 'Odrzucony', icon: XCircle, color: 'bg-red-500' }
};

export default function GalleryDetailPage() {
  const { galleryId } = useParams();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [filterLabel, setFilterLabel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedPhotoForEdit, setSelectedPhotoForEdit] = useState(null);
  const [comparePhotos, setComparePhotos] = useState(null);
  const [uploadVersionFor, setUploadVersionFor] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('photos');
  const [albumLayout, setAlbumLayout] = useState(null);
  const [pricing, setPricing] = useState({ default_price_per_photo: 0, photos_pricing: [] });
  const [savingPricing, setSavingPricing] = useState(false);

  useEffect(() => {
    fetchGalleryData();
  }, [galleryId]);

  const fetchGalleryData = async () => {
    try {
      const [galleryRes, photosRes] = await Promise.all([
        axios.get(`${API}/galleries/${galleryId}`),
        axios.get(`${API}/galleries/${galleryId}/photos`)
      ]);
      setGallery(galleryRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/galleries');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/galleries/${galleryId}/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/galleries/${galleryId}/pricing`, pricing);
      toast.success(language === 'pl' ? 'Cennik zapisany' : 'Pricing saved');
    } catch (error) {
      toast.error(language === 'pl' ? 'Nie udało się zapisać cennika' : 'Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'pricing') {
      fetchPricing();
    }
  }, [activeTab, galleryId]);

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    const newProgress = {};
    
    for (const file of acceptedFiles) {
      newProgress[file.name] = 0;
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(
          `${API}/galleries/${galleryId}/photos`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              setUploadProgress(prev => ({ ...prev, [file.name]: percent }));
            }
          }
        );
        
        setPhotos(prev => [response.data, ...prev]);
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[file.name];
          return updated;
        });
      }
    }
    
    setUploading(false);
    // Clear progress after 2 seconds
    setTimeout(() => {
      setUploadProgress({});
    }, 2000);
    
    // Refresh gallery to update photo count
    fetchGalleryData();
  }, [galleryId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif']
    }
  });

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleAnalyzeAI = async (photoId) => {
    try {
      await axios.post(`${API}/photos/${photoId}/analyze`);
      toast.success(language === 'pl' ? 'Analiza AI rozpoczęta' : 'AI analysis started');
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleUpdateLabel = async (photoId, label) => {
    try {
      await axios.put(`${API}/photos/${photoId}/label`, { label });
      setPhotos(photos.map(p => p.id === photoId ? { ...p, label } : p));
      toast.success(language === 'pl' ? 'Etykieta zaktualizowana' : 'Label updated');
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleUpdateApproval = async (photoId, approvalStatus) => {
    try {
      await axios.put(`${API}/photos/${photoId}/approval`, { approval_status: approvalStatus });
      setPhotos(photos.map(p => p.id === photoId ? { ...p, approval_status: approvalStatus } : p));
      toast.success(language === 'pl' ? 'Status zaktualizowany' : 'Status updated');
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleSetCover = async (photoId) => {
    try {
      const response = await axios.put(`${API}/galleries/${galleryId}/cover`, { photo_id: photoId });
      setGallery(prev => ({ 
        ...prev, 
        cover_photo_id: photoId,
        cover_url: response.data.cover_url 
      }));
      toast.success(language === 'pl' ? 'Zdjęcie główne ustawione' : 'Cover photo set');
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleBatchUpdateLabel = async (label) => {
    for (const photoId of selectedPhotos) {
      await handleUpdateLabel(photoId, label);
    }
    setSelectedPhotos([]);
  };

  const handleBatchUpdateApproval = async (status) => {
    for (const photoId of selectedPhotos) {
      await handleUpdateApproval(photoId, status);
    }
    setSelectedPhotos([]);
  };

  // Filter photos
  const filteredPhotos = photos.filter(p => {
    if (filterLabel !== 'all' && p.label !== filterLabel) return false;
    if (filterStatus !== 'all' && p.approval_status !== filterStatus) return false;
    return true;
  });

  const copyLink = () => {
    const url = `${window.location.origin}/g/${gallery.public_id}`;
    navigator.clipboard.writeText(url);
    toast.success(t('gallery.linkCopied'));
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const deleteSelectedPhotos = async () => {
    if (!window.confirm(t('gallery.deleteConfirm'))) return;
    for (const photoId of selectedPhotos) {
      await handleDeletePhoto(photoId);
    }
    setSelectedPhotos([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/galleries" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/galleries')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            {/* Cover Photo Thumbnail */}
            {gallery?.cover_url && (
              <div className="w-16 h-16 rounded-lg overflow-hidden border border-border">
                <img 
                  src={gallery.cover_url} 
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold">{gallery?.name}</h1>
                {gallery?.billing_tier && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                    gallery.billing_tier === 'S' ? 'bg-blue-500/10 text-blue-500' :
                    gallery.billing_tier === 'M' ? 'bg-purple-500/10 text-purple-500' :
                    'bg-amber-500/10 text-amber-500'
                  }`}>
                    {gallery.billing_tier}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground">{gallery?.client_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={copyLink} data-testid="copy-link-btn">
              <Copy className="w-4 h-4 mr-2" />
              {t('gallery.copyLink')}
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.open(`/g/${gallery?.public_id}`, '_blank')}
              data-testid="view-gallery-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('gallery.viewGallery')}
            </Button>
            <Button 
              variant="secondary"
              onClick={() => setShareModalOpen(true)}
              data-testid="share-gallery-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {language === 'pl' ? 'Udostępnij' : 'Share'}
            </Button>
          </div>
        </div>

        {/* Billing tier pending warning */}
        {gallery?.billing_tier_pending && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm flex-1">
              {language === 'pl'
                ? `Ta galeria wymaga upgrade do tieru ${gallery.billing_tier_pending}. `
                : `This gallery needs an upgrade to tier ${gallery.billing_tier_pending}. `}
              <a href="/billing" className="text-primary underline">
                {language === 'pl' ? 'Dokup kredyty' : 'Buy credits'}
              </a>
            </p>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="flex items-center gap-1 mb-6 border-b border-border overflow-x-auto pb-px">
          <Button
            variant={activeTab === 'photos' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('photos')}
            className="flex items-center gap-2"
          >
            <Images className="w-4 h-4" />
            {language === 'pl' ? 'Zdjęcia' : 'Photos'}
            <Badge variant="secondary" className="ml-1">{photos.length}</Badge>
          </Button>
          <Button
            variant={activeTab === 'selections' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('selections')}
            className="flex items-center gap-2"
          >
            <Star className="w-4 h-4" />
            {language === 'pl' ? 'Selekcje' : 'Selections'}
          </Button>
          <Button
            variant={activeTab === 'curator' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('curator')}
            className="flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            {language === 'pl' ? 'Kurator' : 'Curator'}
          </Button>
          <Button
            variant={activeTab === 'album' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('album')}
            className="flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            {language === 'pl' ? 'Album' : 'Album'}
          </Button>
          <Button
            variant={activeTab === 'pricing' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('pricing')}
            className="flex items-center gap-2"
          >
            <DollarSign className="w-4 h-4" />
            {language === 'pl' ? 'Cennik' : 'Pricing'}
          </Button>
          <Button
            variant={activeTab === 'stats' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('stats')}
            className="flex items-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            {language === 'pl' ? 'Statystyki' : 'Stats'}
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'photos' && (
          <>
        {/* Upload Zone */}
        <Card className="bg-card border-border mb-8">
          <CardContent className="p-0">
            <div
              {...getRootProps()}
              className={`p-12 border-2 border-dashed rounded-sm text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
              data-testid="photo-dropzone"
            >
              <input {...getInputProps()} />
              <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <p className="font-medium mb-1">
                {isDragActive ? t('gallery.dropzoneActive') : t('gallery.dropzone')}
              </p>
              <p className="text-sm text-muted-foreground">JPEG, PNG, WebP, GIF</p>
            </div>
          </CardContent>
        </Card>

        {/* Upload Progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <Card className="bg-card border-border mb-8">
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-3">{t('gallery.uploadProgress')}</p>
              {Object.entries(uploadProgress).map(([filename, progress]) => (
                <div key={filename} className="mb-3 last:mb-0">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="truncate max-w-[200px]">{filename}</span>
                    <span className="flex items-center gap-1">
                      {progress === 100 ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        `${progress}%`
                      )}
                    </span>
                  </div>
                  <Progress value={progress} className="h-1" />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Actions Bar */}
        {selectedPhotos.length > 0 && (
          <div className="flex items-center gap-4 mb-6 p-4 bg-card border border-border rounded-sm flex-wrap">
            <span className="text-sm font-medium">
              {t('common.selected')}: {selectedPhotos.length}
            </span>
            
            {/* Batch Label */}
            <Select onValueChange={(val) => handleBatchUpdateLabel(val === 'none' ? null : val)}>
              <SelectTrigger className="w-[140px] h-8">
                <Tag className="w-4 h-4 mr-1" />
                <SelectValue placeholder={language === 'pl' ? 'Etykieta' : 'Label'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{language === 'pl' ? 'Brak' : 'None'}</SelectItem>
                {Object.entries(PHOTO_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: label.color }} />
                      {label.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Batch Status */}
            <Select onValueChange={handleBatchUpdateApproval}>
              <SelectTrigger className="w-[160px] h-8">
                <FileCheck className="w-4 h-4 mr-1" />
                <SelectValue placeholder={language === 'pl' ? 'Status' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(APPROVAL_STATUSES).map(([key, status]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <status.icon className="w-3 h-3" />
                      {status.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant="destructive" 
              size="sm"
              onClick={deleteSelectedPhotos}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('gallery.deleteSelected')}
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSelectedPhotos([])}
            >
              {t('gallery.deselectAll')}
            </Button>
          </div>
        )}

        {/* Filter Bar */}
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filterLabel} onValueChange={setFilterLabel}>
              <SelectTrigger className="w-[150px] h-8">
                <SelectValue placeholder={language === 'pl' ? 'Etykieta' : 'Label'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'pl' ? 'Wszystkie etykiety' : 'All labels'}</SelectItem>
                {Object.entries(PHOTO_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: label.color }} />
                      {label.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px] h-8">
                <SelectValue placeholder={language === 'pl' ? 'Status' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'pl' ? 'Wszystkie statusy' : 'All statuses'}</SelectItem>
                {Object.entries(APPROVAL_STATUSES).map(([key, status]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <status.icon className="w-3 h-3" />
                      {status.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <span className="text-sm text-muted-foreground ml-auto">
            {filteredPhotos.length} / {photos.length} {language === 'pl' ? 'zdjęć' : 'photos'}
          </span>
        </div>

        {/* Photos Grid */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {t('gallery.photos')} ({photos.length})
          </h2>
          {photos.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSelectedPhotos(
                selectedPhotos.length === photos.length 
                  ? [] 
                  : photos.map(p => p.id)
              )}
            >
              {selectedPhotos.length === photos.length ? t('gallery.deselectAll') : t('gallery.selectAll')}
            </Button>
          )}
        </div>

        {filteredPhotos.length > 0 ? (
          <div className="masonry-grid">
            {filteredPhotos.map((photo, idx) => (
              <motion.div
                key={photo.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: idx * 0.02 }}
                className="masonry-item"
              >
                <div className={`photo-card rounded-sm overflow-hidden border ${
                  selectedPhotos.includes(photo.id) ? 'border-primary' : 'border-border'
                }`}>
                  <div className="relative group">
                    <img
                      src={photo.thumbnail_url || photo.url}
                      alt={photo.original_filename}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                    
                    {/* Selection Checkbox */}
                    <div 
                      className={`absolute top-3 left-3 w-6 h-6 rounded-sm border-2 flex items-center justify-center cursor-pointer transition-all ${
                        selectedPhotos.includes(photo.id) 
                          ? 'bg-primary border-primary' 
                          : 'bg-black/50 border-white/50 opacity-0 group-hover:opacity-100'
                      }`}
                      onClick={() => togglePhotoSelection(photo.id)}
                    >
                      {selectedPhotos.includes(photo.id) && (
                        <Check className="w-4 h-4 text-primary-foreground" />
                      )}
                    </div>

                    {/* Label Badge */}
                    {photo.label && PHOTO_LABELS[photo.label] && (
                      <div 
                        className="absolute top-3 left-12 w-4 h-4 rounded-full border-2 border-white"
                        style={{ backgroundColor: PHOTO_LABELS[photo.label].color }}
                        title={PHOTO_LABELS[photo.label].name}
                      />
                    )}

                    {/* Approval Status Badge */}
                    {photo.approval_status && APPROVAL_STATUSES[photo.approval_status] && (
                      <div className={`absolute top-3 right-12 px-2 py-0.5 rounded-sm text-xs text-white ${APPROVAL_STATUSES[photo.approval_status].color} opacity-90`}>
                        {APPROVAL_STATUSES[photo.approval_status].name}
                      </div>
                    )}

                    {/* Cover Photo Badge */}
                    {gallery?.cover_photo_id === photo.id && (
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-sm text-xs bg-primary text-primary-foreground flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        {language === 'pl' ? 'Okładka' : 'Cover'}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        variant="secondary" 
                        size="icon"
                        className={`h-7 w-7 ${gallery?.cover_photo_id === photo.id ? 'bg-primary hover:bg-primary/80' : 'bg-black/70 hover:bg-black'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetCover(photo.id);
                        }}
                        title={language === 'pl' ? 'Ustaw jako okładkę' : 'Set as cover'}
                      >
                        <Star className={`w-4 h-4 ${gallery?.cover_photo_id === photo.id ? 'fill-current' : ''}`} />
                      </Button>
                      <Button 
                        variant="secondary" 
                        size="icon"
                        className="h-7 w-7 bg-black/70 hover:bg-black"
                        onClick={() => setSelectedPhotoForEdit(photo)}
                        title={language === 'pl' ? 'Edytuj' : 'Edit'}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="secondary" 
                        size="icon"
                        className="h-7 w-7 bg-black/70 hover:bg-black"
                        onClick={() => handleAnalyzeAI(photo.id)}
                        title={t('gallery.analyzeAI')}
                      >
                        <Sparkles className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="secondary" 
                        size="icon"
                        className="h-7 w-7 bg-black/70 hover:bg-destructive"
                        onClick={() => handleDeletePhoto(photo.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Favorite Badge */}
                    {photo.is_favorite && (
                      <div className="absolute bottom-3 left-3 bg-black/70 rounded-sm px-2 py-1">
                        <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                      </div>
                    )}

                    {/* AI Tags */}
                    {photo.ai_tags?.length > 0 && (
                      <div className="absolute bottom-3 right-3 flex gap-1 flex-wrap justify-end max-w-[70%]">
                        {photo.ai_tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="bg-black/70 text-xs px-2 py-0.5 rounded-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm truncate">{photo.original_filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {(photo.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Images className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">{t('gallery.noPhotos')}</h3>
              <p className="text-muted-foreground">
                {language === 'pl' ? 'Przeciągnij zdjęcia aby dodać' : 'Drag photos to add'}
              </p>
            </CardContent>
          </Card>
        )}
          </>
        )}

        {/* Selections Tab */}
        {activeTab === 'selections' && (
          <SelectionViewer galleryId={galleryId} photos={photos} />
        )}

        {/* Curator Tab */}
        {activeTab === 'curator' && (
          <CuratorView
            galleryId={galleryId}
            photos={photos}
            onPhotosUpdate={fetchGalleryData}
          />
        )}

        {/* Album Tab */}
        {activeTab === 'album' && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant={!albumLayout ? 'default' : 'outline'}
                onClick={() => setAlbumLayout(null)}
              >
                <Wand2 className="w-4 h-4 mr-2" />
                {language === 'pl' ? 'Kreator' : 'Designer'}
              </Button>
              {albumLayout && (
                <Button
                  variant="outline"
                  onClick={() => {}}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  {language === 'pl' ? 'Podgląd albumu' : 'Album Preview'}
                </Button>
              )}
            </div>
            {!albumLayout ? (
              <MagicDesigner
                galleryId={galleryId}
                photos={photos}
                onLayoutGenerated={(layout) => setAlbumLayout(layout)}
              />
            ) : (
              <BookView
                layout={albumLayout}
                photos={photos}
                coverUrl={gallery?.cover_url}
              />
            )}
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                {language === 'pl' ? 'Cennik zdjęć' : 'Photo Pricing'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>{language === 'pl' ? 'Domyślna cena za zdjęcie (PLN)' : 'Default price per photo (PLN)'}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pricing.default_price_per_photo || ''}
                  onChange={(e) => setPricing({ ...pricing, default_price_per_photo: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="max-w-xs"
                />
                <p className="text-sm text-muted-foreground">
                  {language === 'pl'
                    ? 'Ta cena będzie stosowana do wszystkich zdjęć, chyba że ustawisz indywidualną cenę.'
                    : 'This price will be applied to all photos unless you set individual prices.'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>{language === 'pl' ? 'Indywidualne ceny zdjęć' : 'Individual photo prices'}</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3 max-h-96 overflow-y-auto">
                  {photos.map((photo) => {
                    const photoPrice = pricing.photos_pricing?.find(p => p.photo_id === photo.id);
                    return (
                      <div key={photo.id} className="space-y-1">
                        <div className="aspect-square rounded overflow-hidden border border-border">
                          <img
                            src={photo.thumbnail_url || photo.url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={pricing.default_price_per_photo?.toString() || '0'}
                          value={photoPrice?.price || ''}
                          onChange={(e) => {
                            const newPrice = parseFloat(e.target.value);
                            const existingPricing = pricing.photos_pricing?.filter(p => p.photo_id !== photo.id) || [];
                            if (newPrice > 0) {
                              setPricing({
                                ...pricing,
                                photos_pricing: [...existingPricing, { photo_id: photo.id, price: newPrice }]
                              });
                            } else {
                              setPricing({ ...pricing, photos_pricing: existingPricing });
                            }
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button onClick={savePricing} disabled={savingPricing}>
                {savingPricing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {language === 'pl' ? 'Zapisz cennik' : 'Save pricing'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                {language === 'pl' ? 'Statystyki galerii' : 'Gallery Stats'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <div className="text-3xl font-bold">{gallery?.views || 0}</div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'pl' ? 'Wyświetlenia' : 'Views'}
                  </div>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <div className="text-3xl font-bold">{gallery?.downloads || 0}</div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'pl' ? 'Pobrania' : 'Downloads'}
                  </div>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <div className="text-3xl font-bold">{photos.length}</div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'pl' ? 'Zdjęcia' : 'Photos'}
                  </div>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <div className="text-3xl font-bold">
                    {photos.filter(p => p.approval_status === 'approved').length}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'pl' ? 'Zatwierdzone' : 'Approved'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Photo Edit Modal */}
      <AnimatePresence>
        {selectedPhotoForEdit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setSelectedPhotoForEdit(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-card rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold">
                    {language === 'pl' ? 'Edytuj zdjęcie' : 'Edit Photo'}
                  </h3>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setSelectedPhotoForEdit(null)}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                
                {/* Photo Preview */}
                <div className="mb-6">
                  <img 
                    src={selectedPhotoForEdit.thumbnail_url || selectedPhotoForEdit.url} 
                    alt={selectedPhotoForEdit.original_filename}
                    className="w-full h-48 object-cover rounded-sm"
                  />
                  <p className="text-sm text-muted-foreground mt-2">{selectedPhotoForEdit.original_filename}</p>
                </div>
                
                {/* Label Selection */}
                <div className="mb-6">
                  <Label className="mb-3 block">{language === 'pl' ? 'Etykieta kolorystyczna' : 'Color Label'}</Label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleUpdateLabel(selectedPhotoForEdit.id, null)}
                      className={`px-3 py-1.5 rounded-sm border text-sm ${
                        !selectedPhotoForEdit.label ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {language === 'pl' ? 'Brak' : 'None'}
                    </button>
                    {Object.entries(PHOTO_LABELS).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => {
                          handleUpdateLabel(selectedPhotoForEdit.id, key);
                          setSelectedPhotoForEdit({ ...selectedPhotoForEdit, label: key });
                        }}
                        className={`px-3 py-1.5 rounded-sm border text-sm flex items-center gap-2 ${
                          selectedPhotoForEdit.label === key ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: label.color }} />
                        {label.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Approval Status */}
                <div className="mb-6">
                  <Label className="mb-3 block">{language === 'pl' ? 'Status zatwierdzenia' : 'Approval Status'}</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(APPROVAL_STATUSES).map(([key, status]) => {
                      const StatusIcon = status.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            handleUpdateApproval(selectedPhotoForEdit.id, key);
                            setSelectedPhotoForEdit({ ...selectedPhotoForEdit, approval_status: key });
                          }}
                          className={`px-3 py-1.5 rounded-sm border text-sm flex items-center gap-2 ${
                            selectedPhotoForEdit.approval_status === key ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <StatusIcon className="w-4 h-4" />
                          {status.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Photo Info */}
                <div className="border-t border-border pt-4">
                  <h4 className="font-medium mb-3">{language === 'pl' ? 'Informacje' : 'Info'}</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">{language === 'pl' ? 'Rozmiar' : 'Size'}:</span>
                      <span className="ml-2">{(selectedPhotoForEdit.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    {selectedPhotoForEdit.width && selectedPhotoForEdit.height && (
                      <div>
                        <span className="text-muted-foreground">{language === 'pl' ? 'Wymiary' : 'Dimensions'}:</span>
                        <span className="ml-2">{selectedPhotoForEdit.width} x {selectedPhotoForEdit.height}</span>
                      </div>
                    )}
                    {selectedPhotoForEdit.ai_tags?.length > 0 && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">{language === 'pl' ? 'Tagi AI' : 'AI Tags'}:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedPhotoForEdit.ai_tags.map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Version History */}
                {selectedPhotoForEdit.versions?.length > 0 && (
                  <div className="border-t border-border pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <History className="w-4 h-4" />
                        {language === 'pl' ? 'Historia wersji' : 'Version History'}
                      </h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (selectedPhotoForEdit.versions.length >= 2) {
                            setComparePhotos({
                              before: selectedPhotoForEdit.versions[0].url,
                              after: selectedPhotoForEdit.url
                            });
                          }
                        }}
                        disabled={selectedPhotoForEdit.versions.length < 1}
                      >
                        <GitCompare className="w-4 h-4 mr-2" />
                        {language === 'pl' ? 'Porównaj' : 'Compare'}
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedPhotoForEdit.versions.map((version, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-2 bg-secondary/50 rounded-sm text-sm"
                        >
                          <span>v{version.version || idx + 1}</span>
                          <span className="text-muted-foreground">
                            {new Date(version.created_at).toLocaleDateString('pl-PL')}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setComparePhotos({
                              before: version.url,
                              after: selectedPhotoForEdit.url
                            })}
                          >
                            <GitCompare className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo Compare Slider */}
      {comparePhotos && (
        <PhotoCompareSlider
          beforeUrl={comparePhotos.before}
          afterUrl={comparePhotos.after}
          beforeLabel={language === 'pl' ? 'Przed' : 'Before'}
          afterLabel={language === 'pl' ? 'Po' : 'After'}
          onClose={() => setComparePhotos(null)}
        />
      )}

      {/* Share Gallery Modal */}
      <ShareGalleryModal 
        galleryId={galleryId}
        galleryName={gallery?.name}
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        language={language}
      />
    </div>
  );
}
